/**
 * Shared skill picker modal (P4.T3).
 *
 * Used by both sections of SkillsCard (Default + Auto-load) via the
 * `mode` prop. Click a catalog row → composerStore.addSkill(name, mode)
 * → onOpenChange(false). Filters out skills already installed in EITHER
 * list (defensive; the card-side "+ Add" button shouldn't open the
 * modal for an already-full catalog, but the filter keeps the UX
 * honest).
 *
 * Reuses the existing `@/components/ui/Modal` primitive. No new layout
 * primitives — Modal is the right shape for a rich-metadata picker
 * (description + required_capabilities + version per row).
 *
 * P4.T9 ("Explain why" affordances) will layer a satisfaction
 * indicator onto the per-row required-capabilities text — the picker's
 * DOM is intentionally additive-compatible with that overlay.
 */
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui'
import { useComposerStore, type SkillMode } from '@/stores/composerStore'
import { useSkillCatalog, type SkillCatalogEntry } from '@/hooks/useSkillCatalog'

export interface SkillPickerModalProps {
  open: boolean
  mode: SkillMode
  onOpenChange: (open: boolean) => void
}

export function SkillPickerModal({ open, mode, onOpenChange }: SkillPickerModalProps) {
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const addSkill = useComposerStore((s) => s.addSkill)
  const query = useSkillCatalog()

  const base = draft ?? server
  const installed = new Set<string>(
    base
      ? [
          ...base.default_skills.map((s) => s.name),
          ...base.auto_skills.map((s) => s.name),
        ]
      : [],
  )

  const catalog = query.data ?? []
  const available = catalog.filter((s) => !installed.has(s.name) && !s.deprecated)

  const handleSelect = (skill: SkillCatalogEntry) => {
    addSkill(skill.name, mode)
    onOpenChange(false)
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            Add a skill ({mode === 'default' ? 'always on' : 'auto-load'})
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {query.isLoading && (
            <div className="space-y-3" data-testid="catalog-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 rounded-sm" />
              ))}
            </div>
          )}
          {query.isError && (
            <Alert variant="destructive" className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span>Unable to load the skill catalog.</span>
                <Button variant="ghost" size="sm" onClick={() => query.refetch()}>
                  Retry
                </Button>
              </div>
            </Alert>
          )}
          {query.isSuccess && available.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No skills available — check your deployment's SKILL.md files
              or all available skills are already installed.
            </p>
          )}
          {query.isSuccess && available.length > 0 && (
            <ul className="space-y-2">
              {available.map((skill) => (
                <li key={skill.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(skill)}
                    className="w-full text-left p-3 rounded-sm border border-border hover:bg-muted transition"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-semibold text-foreground">
                        {skill.display_name}
                      </h3>
                      <span className="text-xs text-muted-foreground shrink-0">
                        v{skill.version}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {skill.description}
                    </p>
                    {skill.required_capabilities.length > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
                        Requires: {skill.required_capabilities.join(', ')}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
